import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import GuardianAuth from '@amzn/GuardianAuth/lib/auth/GuardianAuth';
import ToastErrorMessage from '@amzn/GuardianWidgetCommons/lib/components/ToastErrorMessage';
import asyncRequest from '@amzn/GuardianWidgetCommons/lib/helper/asyncRequest';
import React from 'react';
import { toast } from 'react-toastify';
import { AppThunk } from '../../context/store';
import { SITE_SHUTDOWNS_API_ENDPOINT, SORT_ORDER_DIRECTIONS } from '../../lib/globals';
import { DateRange, ShutdownStatus, SiteShutdown, SiteShutdownApiDataInstance } from '../../lib/types';
import { Logger } from '../../lib/utils/logger';
import { durationToString } from '../../components/DataGrids/SiteShutdownsProps';
import { ColumnProps, createCSV } from '../../lib/helpers/CSVHelper';
import {
    SiteShutdownsPropMap,
    SiteShutdownsTitlePropMap
} from '../../lib/dictionaries/SiteShutdownsDictionary';
import { formatToLocalTime } from '../../lib/helpers/helpers';
import { DateTime } from 'luxon';

const { doRequest } = asyncRequest;

// 1. Define the SearchSiteShutdownFilterAttributes and SearchSiteSortOrder
type StatusCounts = Record<ShutdownStatus, number>;
type StatusFilters = Partial<Record<ShutdownStatus, SearchSiteShutdownFilterAttributeItem>>;

const in48hours = () => {
    const now = new Date();
    return new Date(now.getTime() + 48 * 60 * 60 * 1000);
};

const twoWeeksAgo = () => {
    const now = new Date();
    return new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
};

interface SearchSiteShutdownFilterAttributes {
    regularFilters: SearchSiteShutdownFilterAttributeItem;
    statusFilters: StatusFilters;
}

interface SearchSiteShutdownFilterAttributeItem {
    [name: string]: (string | DateRange)[];
}

interface SearchSiteShutdownSortOrder {
    [index: number]: {
        column: string; // which column to sort by
        order: string; // ASC or DESC
    };
}

// 2. Define the interface of the inital state
interface SiteShutdownState {
    siteShutdowns: SiteShutdown[];
    totalCount: number;
    pageSize: number;
    pageNumber: number;
    filterAttributes: SearchSiteShutdownFilterAttributes | null;
    sortOrder: SearchSiteShutdownSortOrder;
    statusTotalCounts: StatusCounts;
    isLoading: boolean;
    searchError: string;
}

// Same as above but took out isLoading and searchError because those should not be passed as arguments/payloads.
interface SiteShutdownStatePayload {
    siteShutdowns: SiteShutdown[];
    totalCount: number;
    pageSize: number;
    pageNumber: number;
    filterAttributes: SearchSiteShutdownFilterAttributes | null;
    sortOrder: SearchSiteShutdownSortOrder;
    statusTotalCounts: StatusCounts;
}

// 3. Define the initial Redux State
const initialState: SiteShutdownState = {
    siteShutdowns: [],
    totalCount: 0,
    pageSize: 25,
    pageNumber: 1,
    filterAttributes: null,
    sortOrder: [{ column: 'startTime', order: SORT_ORDER_DIRECTIONS.DESCENDING }],
    statusTotalCounts: {
        Scheduled: 0,
        Upcoming: 0,
        Ongoing: 0,
        Reopened: 0,
        Completed: 0,
        Canceled: 0
    },
    isLoading: false,
    searchError: ''
};

// 4. Define the slice with reducers
const siteShutdownSlice = createSlice({
    name: 'siteShutdown',
    initialState,
    reducers: {
        startSearchSiteShutdowns: (state) => {
            const { siteShutdowns, searchError } = initialState;
            state.siteShutdowns = siteShutdowns;
            state.searchError = searchError;
            state.isLoading = true;
        },
        searchSiteShutdownsSuccess: (state, { payload }: PayloadAction<SiteShutdownStatePayload>) => {
            const {
                siteShutdowns,
                totalCount,
                pageSize,
                pageNumber,
                filterAttributes,
                sortOrder,
                statusTotalCounts
            } = payload;
            state.siteShutdowns = siteShutdowns;
            state.totalCount = totalCount;
            state.pageSize = pageSize;
            state.pageNumber = pageNumber;
            state.filterAttributes = filterAttributes;
            state.sortOrder = sortOrder;
            state.statusTotalCounts = statusTotalCounts;
            state.isLoading = false;
            state.searchError = '';
        },
        searchSiteShutdownsFailed: (state, { payload }: PayloadAction<{ error: string }>) => {
            const { error } = payload;
            state.isLoading = false;
            state.searchError = error;
        }
    }
});

// 4.1 Get the actions and export default the reducer
export const {
    startSearchSiteShutdowns,
    searchSiteShutdownsSuccess,
    searchSiteShutdownsFailed
} = siteShutdownSlice.actions;

export default siteShutdownSlice.reducer;

// 5. Async Helper function calling API
interface SearchSiteShutdownsHelperResult {
    totalCount: number;
    siteShutdowns: SiteShutdown[];
}

const searchSiteShutdownsHelper = async (
    pageNumber: number,
    pageSize: number,
    filterAttributes: SearchSiteShutdownFilterAttributes | null,
    sortOrder: SearchSiteShutdownSortOrder
): Promise<SearchSiteShutdownsHelperResult> => {
    const request = {
        pageNumber,
        pageSize,
        filterAttributes,
        sortOrder
    };

    const response = await doRequest(
        SITE_SHUTDOWNS_API_ENDPOINT,
        request,
        GuardianAuth.createRequestAuthHeader()
    );

    // response.data is already parsed as an object by Axios
    const { data } = response;

    const totalCount = data.total;
    const dataRows = data.data;

    const fsiteShutdownData: SiteShutdown[] = [];
    let status: ShutdownStatus;
    dataRows.forEach((instance: SiteShutdownApiDataInstance) => {
        let {
            id,
            preCogId,
            siteCountry,
            siteState,
            siteCity,
            siteId,
            siteName,
            facilityType,
            businessLine,
            eventType,
            startTime,
            endTime,
            duration,
            reason,
            siteType,
            lastUpdateTime
        } = instance;
        status = determineStatus(eventType, startTime, endTime);
        fsiteShutdownData.push({
            id,
            preCogId,
            siteCountry,
            siteState,
            siteCity,
            siteId,
            siteName,
            facilityType,
            businessLine,
            eventType,
            status,
            startTime,
            endTime,
            duration,
            reason,
            siteType,
            lastUpdateTime
        });
    });
    return { totalCount, siteShutdowns: fsiteShutdownData };
};

// 6. Function to determine client-side status
export function determineStatus(eventType: string, startTime: string, endTime: string): ShutdownStatus {
    const currentTime = new Date();
    const start = new Date(startTime);

    const end = new Date(endTime);
    const twoWeeksInMs = 1209600000;
    const fortyEightHoursInMs = 172800000;

    switch (eventType) {
        case 'SiteShutdownSchedule':
            if (start.getTime() - currentTime.getTime() < fortyEightHoursInMs) {
                return ShutdownStatus.UPCOMING;
            } else {
                return ShutdownStatus.SCHEDULED;
            }
        case 'SiteShutdownUnderway':
            return ShutdownStatus.ONGOING;
        case 'SiteShutdownOver':
            if (currentTime.getTime() - end.getTime() < twoWeeksInMs) {
                return ShutdownStatus.REOPENED;
            } else {
                return ShutdownStatus.COMPLETED;
            }
        case 'SiteShutdownCancel':
            return ShutdownStatus.CANCELED;
        default:
            return ShutdownStatus.SCHEDULED;
    }
}

// 7. The actual Async function that interacts with components
export const searchSiteShutdownsAsync = (
    pageNumber: number,
    pageSize: number,
    regularFilters: SearchSiteShutdownFilterAttributeItem,
    statuses: ShutdownStatus[],
    sortOrder: SearchSiteShutdownSortOrder
): AppThunk => async (dispatch) => {
    dispatch(startSearchSiteShutdowns());

    try {
        let baseFilterAttribute: SearchSiteShutdownFilterAttributes;
        let statusFilters: StatusFilters = {};
        if (statuses.length > 0) {
            statuses.forEach((status) => (statusFilters[status] = quickFilters[status]));
            baseFilterAttribute = { regularFilters, statusFilters };
        } else {
            baseFilterAttribute = { regularFilters, statusFilters };
        }
        let queriesToRun: Promise<SearchSiteShutdownsHelperResult>[] = [
            searchSiteShutdownsHelper(pageNumber, pageSize, baseFilterAttribute, sortOrder)
        ];

        for (const status of Object.values(ShutdownStatus)) {
            statusFilters = {};
            statusFilters[status] = quickFilters[status];
            const combinedFilters = { regularFilters, statusFilters };
            queriesToRun = [...queriesToRun, searchSiteShutdownsHelper(1, 1, combinedFilters, sortOrder)];
        }

        const [mainResult, ...statusTotalCountsData] = await Promise.all(queriesToRun);

        const statusKeys = Object.values(ShutdownStatus);

        const statusTotalCountsArray = statusKeys.map((key, idx) => {
            const { totalCount } = statusTotalCountsData[idx];
            return [key, totalCount];
        });

        const statusTotalCounts: StatusCounts = Object.fromEntries(statusTotalCountsArray);

        const { totalCount, siteShutdowns } = mainResult;

        dispatch(
            searchSiteShutdownsSuccess({
                totalCount,
                siteShutdowns,
                pageNumber,
                pageSize,
                filterAttributes: baseFilterAttribute,
                sortOrder,
                statusTotalCounts
            })
        );
    } catch (error) {
        let message = error.message;
        const { response } = error;
        if (response) {
            const parsedBody = JSON.parse(response.data.body);
            const { error: returnedErrorMessage } = parsedBody;
            message = returnedErrorMessage;
        }
        dispatch(searchSiteShutdownsFailed({ error: message }));
        toast.error(
            <ToastErrorMessage header={'Search site shutdowns failed'} errorMessage={error.message} />
        );
    }
};

export const quickFilters: Record<ShutdownStatus, SearchSiteShutdownFilterAttributeItem> = {
    Scheduled: {
        eventType: ['SiteShutdownSchedule'],
        startTime: [{ from: in48hours(), to: null }]
    },
    Upcoming: {
        eventType: ['SiteShutdownSchedule'],
        startTime: [{ from: null, to: in48hours() }]
    },
    Ongoing: {
        eventType: ['SiteShutdownUnderway']
    },
    Reopened: {
        eventType: ['SiteShutdownOver'],
        endTime: [{ from: twoWeeksAgo(), to: null }]
    },
    Completed: {
        eventType: ['SiteShutdownOver'],
        endTime: [{ from: null, to: twoWeeksAgo() }]
    },
    Canceled: {
        eventType: ['SiteShutdownCancel']
    }
};

export const downloadSiteShutdownsCSVAsync = (
    filename: string,
    filterAttributes: SearchSiteShutdownFilterAttributes | null,
    sortOrder: SearchSiteShutdownSortOrder
): AppThunk => async (dispatch) => {
    const toastId = toast.info('Starting to download Site Shutdowns CSV');
    try {
        let totalCount = -1;
        const pageSize = 50;
        const fullSiteShutdowns: SiteShutdown[] = [];
        for (let pageNumber = 1; pageNumber == 1 || totalCount > (pageNumber - 1) * pageSize; pageNumber++) {
            const result = await searchSiteShutdownsHelper(pageNumber, pageSize, filterAttributes, sortOrder);
            const { siteShutdowns, totalCount: returnedTotalCount } = result;
            fullSiteShutdowns.push(...siteShutdowns);
            totalCount = returnedTotalCount;
        }
        const formattedFullSiteShutdowns = fullSiteShutdowns.map((row: any) => {
            const shutdown = { ...row };
            console.log(JSON.stringify(row));

            if (typeof shutdown.duration === 'number') {
                shutdown.duration = durationToString(shutdown.duration);
            }

            if (typeof shutdown.lastUpdateTime === 'number') {
                shutdown.lastUpdateTime = formatToLocalTime(DateTime.fromMillis(shutdown.lastUpdateTime));
            }

            return shutdown;
        });
        const columnProps: ColumnProps = {
            columns: [
                SiteShutdownsTitlePropMap.id,
                SiteShutdownsTitlePropMap.country,
                SiteShutdownsTitlePropMap.state,
                SiteShutdownsTitlePropMap.city,
                SiteShutdownsTitlePropMap.sites,
                SiteShutdownsTitlePropMap.facilityType,
                SiteShutdownsTitlePropMap.businessLine,
                SiteShutdownsTitlePropMap.status,
                SiteShutdownsTitlePropMap.start,
                SiteShutdownsTitlePropMap.end,
                SiteShutdownsTitlePropMap.reasons,
                SiteShutdownsTitlePropMap.shutdownAreas,
                SiteShutdownsTitlePropMap.duration,
                SiteShutdownsTitlePropMap.lastUpdateTime
            ],
            columnDataMap: {
                [SiteShutdownsTitlePropMap.id]: SiteShutdownsPropMap.id,
                [SiteShutdownsTitlePropMap.country]: SiteShutdownsPropMap.country,
                [SiteShutdownsTitlePropMap.state]: SiteShutdownsPropMap.state,
                [SiteShutdownsTitlePropMap.city]: SiteShutdownsPropMap.city,
                [SiteShutdownsTitlePropMap.sites]: SiteShutdownsPropMap.sites,
                [SiteShutdownsTitlePropMap.facilityType]: SiteShutdownsPropMap.facilityType,
                [SiteShutdownsTitlePropMap.businessLine]: SiteShutdownsPropMap.businessLine,
                [SiteShutdownsTitlePropMap.status]: SiteShutdownsPropMap.status,
                [SiteShutdownsTitlePropMap.start]: SiteShutdownsPropMap.start,
                [SiteShutdownsTitlePropMap.end]: SiteShutdownsPropMap.end,
                [SiteShutdownsTitlePropMap.reasons]: SiteShutdownsPropMap.reasons,
                [SiteShutdownsTitlePropMap.shutdownAreas]: SiteShutdownsPropMap.shutdownAreas,
                [SiteShutdownsTitlePropMap.duration]: SiteShutdownsPropMap.duration,
                [SiteShutdownsTitlePropMap.lastUpdateTime]: SiteShutdownsPropMap.lastUpdateTime
            }
        };
        const csvContent = await createCSV(formattedFullSiteShutdowns, columnProps);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) {
            navigator.msSaveBlob(blob, filename);
        } else {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('visibility', 'hidden');
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        toast.update(toastId, {
            render: 'Downloaded Site Shutdowns CSV Successfully',
            type: toast.TYPE.SUCCESS
        });
        Logger.info(`Downloaded Site Shutdowns Successfully`);
    } catch (error) {
        dispatch(searchSiteShutdownsFailed({ error: error.message }));
        toast.update(toastId, {
            render: (
                <ToastErrorMessage
                    header={'Failed to Download Site Shutdowns'}
                    errorMessage={error.message}
                />
            ),
            type: toast.TYPE.ERROR,
            autoClose: false
        });
        Logger.error(`Failed to download Site Shutdowns with error: ${error.message}`);
    }
};
